import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { MENU, CONTACT, LOGO } from "@/data/content";
import { EASE } from "@/components/Reveal";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hoverMenu, setHoverMenu] = useState(null);
  const location = useLocation();

  const isActive = (item) =>
    location.pathname === item.to ||
    (item.children && item.children.some((c) => c.to === location.pathname));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 border-b border-white/10 ${
          scrolled || open ? "shadow-lg shadow-black/30" : ""
        }`}
        style={{ backgroundColor: "#0B1B30" }}
        data-testid="navbar"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo">
            <span className="bg-[#ffffff] rounded-lg px-2.5 py-1.5 inline-flex shadow-sm">
              <img src={LOGO} alt="THECREATOR Education" className="h-8 md:h-9 w-auto" />
            </span>
            <span className="hidden md:inline-flex flex-col leading-tight" data-testid="nav-wordmark">
              <span className="font-display text-white text-base lg:text-lg font-semibold tracking-tight">THECREATOR</span>
              <span className="font-mono-label text-[10px] tracking-label uppercase text-white/60">Education</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" onMouseLeave={() => setHoverMenu(null)}>
            {MENU.map((item) => (
              <div key={item.label} className="relative" onMouseEnter={() => setHoverMenu(item.label)}>
                <Link
                  to={item.to}
                  data-testid={`nav-link-${slug(item.label)}`}
                  className={`inline-flex items-center gap-1 text-[15px] tracking-[0.01em] font-medium transition-colors duration-200 ${
                    isActive(item) ? "text-accent" : "text-white/95 hover:text-accent"
                  }`}
                >
                  {item.label}
                  {item.children && <ChevronDown className="w-4 h-4" />}
                </Link>
                <AnimatePresence>
                  {item.children && hoverMenu === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full pt-4 w-64"
                      data-testid={`nav-dropdown-${slug(item.label)}`}
                    >
                      <div className="backdrop-blur-xl border border-white/10 py-2" style={{ backgroundColor: "rgba(10, 18, 32, 0.97)" }}>
                        {item.children.map((c) => {
                          const isActive = location.pathname === c.to;
                          return (
                            <Link
                              key={c.to}
                              to={c.to}
                              data-testid={`nav-sublink-${slug(c.label)}`}
                              className="group block px-5 py-3 text-[14px] font-medium transition-colors"
                              style={{
                                color: isActive ? "#F0A831" : "rgba(255,255,255,0.95)",
                              }}
                              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = "#F0A831"; }}
                              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = "rgba(255,255,255,0.95)"; }}
                            >
                              {c.label}
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/enroll"
              data-testid="nav-enroll-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-accent text-[#050B14] px-5 py-2.5 font-mono-label text-[11px] tracking-label uppercase font-medium hover:bg-white transition-colors"
            >
              Enroll <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
              className="lg:hidden w-11 h-11 flex items-center justify-center border border-white/15 text-white hover:bg-white hover:text-[#050B14] transition-colors"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[99] bg-[#050B14]"
            data-testid="nav-overlay"
          >
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-28 pb-12 h-full flex flex-col justify-between overflow-y-auto">
              <nav className="flex flex-col">
                {MENU.map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: EASE, delay: 0.08 + i * 0.05 }}
                    className="border-b border-white/10 py-5"
                  >
                    <Link
                      to={item.to}
                      data-testid={`overlay-link-${slug(item.label)}`}
                      className="group inline-flex items-baseline gap-4"
                    >
                      <span className="font-mono-label text-xs text-accent w-8">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-3xl md:text-5xl text-white/85 group-hover:text-accent transition-colors tracking-tight">
                        {item.label}
                      </span>
                    </Link>
                    {item.children && (
                      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 pl-12">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`overlay-sublink-${slug(c.label)}`}
                            className="font-mono-label text-[11px] tracking-label uppercase text-white/50 hover:text-accent transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </nav>
              <div className="flex flex-wrap gap-x-8 gap-y-2 mt-10 font-mono-label text-[11px] tracking-label uppercase text-white/50">
                <a href={`mailto:${CONTACT.email}`} className="hover:text-accent">{CONTACT.email}</a>
                <a href={`tel:${CONTACT.phone}`} className="hover:text-accent">{CONTACT.phone}</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
